import { Template } from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'

export type TemplateSummaryFormData = {
  descriptionTemplateID?: number
  description: string
}

export type TemplateFormData = {
  description: Template['description']
  requirementBaseID: Template['requirementBase']['requirementBaseID']
  statusID: Template['status']['statusID']
  descriptionTemplateID?: number
  summaryTemplates?: TemplateSummaryFormData[]
}

interface TemplateFormValues {
  auditorStatusID: number
  selectedTemplate?: Template
  saveAssesmentAsTemplate?: boolean
  saveSummaryAsTemplate?: boolean
  auditorAssessment?: string
  summary?: string
}

export const useTemplateMutation = (requirementBaseID: number) => {
  const queryClient = useQueryClient()
  const { watch, getValues } = useFormContext<TemplateFormValues>()
  const selectedStatusID = watch('auditorStatusID')
  const selectedTemplate = watch('selectedTemplate')

  const { mutateAsync: createTemplate, isLoading: isTemplateCreating } =
    useMutation(
      (template: TemplateFormData) =>
        apiJSON(`templates`, template, { method: 'POST' }),
      {
        onSuccess: () => queryClient.invalidateQueries('templates'),
      }
    )

  const { mutateAsync: createSummaryTemplate } = useMutation(
    (template: TemplateSummaryFormData) =>
      apiJSON(`templates/crudSummary`, template, { method: 'POST' }),
    {
      onSuccess: () => queryClient.invalidateQueries('templates'),
    }
  )

  const createNewTemplate = useCallback(
    async (auditorAssessment: string) => {
      const newTemplate: TemplateFormData = {
        description: auditorAssessment,
        statusID: selectedStatusID,
        requirementBaseID,
        descriptionTemplateID:
          selectedTemplate && selectedTemplate.descriptionTemplateID,
      }
      await createTemplate(newTemplate)
    },
    [createTemplate, selectedTemplate, selectedStatusID]
  )

  const addSummaryToExistingTemplate = useCallback(
    async (summary: string) => {
      const newTemplate = {
        description: summary,
        descriptionTemplateID: selectedTemplate?.descriptionTemplateID,
      }

      await createSummaryTemplate(newTemplate)
    },
    [createSummaryTemplate, selectedTemplate]
  )

  const createNewTemplateWithSummary = useCallback(
    async (auditorAssessment: string, summary: string) => {
      const newTemplate: TemplateFormData = {
        description: auditorAssessment,
        statusID: selectedStatusID,
        requirementBaseID,
        summaryTemplates: [
          {
            description: summary,
          },
        ],
      }

      await createTemplate(newTemplate)
    },
    [createTemplate, selectedStatusID]
  )

  const handleManageTemplates = async () => {
    const {
      saveAssesmentAsTemplate,
      saveSummaryAsTemplate,
      auditorAssessment,
      summary,
    } = getValues()

    if (!auditorAssessment) {
      return
    }

    const isEnhancedTemplate =
      saveSummaryAsTemplate ||
      (saveAssesmentAsTemplate && saveSummaryAsTemplate)
    const isAssessmentChanged =
      selectedTemplate?.description === auditorAssessment

    if (isEnhancedTemplate) {
      if (selectedTemplate && isAssessmentChanged && summary) {
        return await addSummaryToExistingTemplate(summary)
      }

      if (summary) {
        return await createNewTemplateWithSummary(auditorAssessment, summary)
      }
    }

    if (saveAssesmentAsTemplate && auditorAssessment) {
      return await createNewTemplate(auditorAssessment)
    }
  }

  return {
    handleManageTemplates,
    isTemplateCreating,
  }
}
